import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { connectConversationWS, connectAgentQueueWS } from '../services/aiWebSocket';

const _chimeAudio = new Audio('/chime.wav');
_chimeAudio.volume = 0.7;
function chime() {
  try {
    _chimeAudio.currentTime = 0;
    _chimeAudio.play().catch(() => {});
  } catch { }
}

const API = '/api/ai';

interface Conversation {
  id: string;
  customer_id: string;
  customer_name?: string;
  status: string;
  created_at: string;
  whatsapp_phone_id?: number;
}

interface WAPhone {
  id: number;
  display_name: string;
  phone_number_id: string;
  is_active: boolean;
}

interface Message {
  id: string;
  sender_type: 'CUSTOMER' | 'AGENT' | 'SYSTEM';
  message: string;
  timestamp: string;
}

function avatar(name: string) {
  return (name || '?')[0].toUpperCase();
}

function fmtTime(ts: string) {
  try { return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
}

function fmtListTime(ts: string) {
  try {
    const d = new Date(ts);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { day: '2-digit', month: 'short' });
  } catch { return ''; }
}

// WhatsApp-style tailed bubble
function Bubble({ msg, contactName }: { msg: Message; contactName: string }) {
  const isAgent    = msg.sender_type === 'AGENT';
  const isSystem   = msg.sender_type === 'SYSTEM';

  if (isSystem) return (
    <div style={{ display: 'flex', justifyContent: 'center', margin: '4px 0' }}>
      <div style={{ background: '#fff8e7', color: '#7c6200', fontSize: 11, borderRadius: 6, padding: '4px 10px', maxWidth: '70%', textAlign: 'center' }}>
        {msg.message}
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', justifyContent: isAgent ? 'flex-end' : 'flex-start', margin: '2px 0' }}>
      <div style={{
        position: 'relative',
        background: isAgent ? '#dcf8c6' : '#ffffff',
        color: '#111',
        borderRadius: isAgent ? '12px 2px 12px 12px' : '2px 12px 12px 12px',
        padding: '7px 12px 20px 12px',
        maxWidth: '65%',
        minWidth: 80,
        fontSize: 14,
        lineHeight: 1.5,
        boxShadow: '0 1px 2px rgba(0,0,0,0.13)',
        wordBreak: 'break-word',
      }}>
        {!isAgent && (
          <div style={{ fontSize: 12, fontWeight: 700, color: '#075E54', marginBottom: 2 }}>
            {contactName}
          </div>
        )}
        {msg.message}
        <span style={{
          position: 'absolute', bottom: 5, right: 9,
          fontSize: 10, color: '#999', whiteSpace: 'nowrap',
        }}>
          {fmtTime(msg.timestamp)}
          {isAgent && <span style={{ marginLeft: 4, color: '#53bdeb' }}>✓✓</span>}
        </span>
      </div>
    </div>
  );
}

export default function WhatsAppInbox({ user, onNewMessage }: { user: any; onNewMessage?: () => void }) {
  const token = localStorage.getItem('token');
  const auth  = { headers: { Authorization: `Bearer ${token}` } };

  const [convs, setConvs]         = useState<Conversation[]>([]);
  const [selected, setSelected]   = useState<Conversation | null>(null);
  const [messages, setMessages]   = useState<Message[]>([]);
  const [input, setInput]         = useState('');
  const [sending, setSending]     = useState(false);
  const [search, setSearch]       = useState('');
  const [phones, setPhones]       = useState<WAPhone[]>([]);
  const [filterPhone, setFilter]  = useState<number | null>(null);

  const convWsRef  = useRef<any>(null);
  const queueWsRef = useRef<any>(null);
  const bottomRef  = useRef<HTMLDivElement>(null);
  const inputRef   = useRef<HTMLTextAreaElement>(null);

  // ── Load all open WhatsApp conversations ──────────────────────────────────
  const loadConvs = useCallback(async () => {
    const [waiting, mine] = await Promise.all([
      axios.get(`${API}/conversations`, { params: { status: 'WAITING_AGENT', channel: 'whatsapp', page_size: 50 }, ...auth }),
      axios.get(`${API}/conversations`, { params: { status: 'AGENT_ASSIGNED', channel: 'whatsapp', assigned_agent: user.id, page_size: 50 }, ...auth }),
    ]);
    const all = [
      ...(waiting.data.items || []),
      ...(mine.data.items   || []),
    ];
    // deduplicate by id
    const seen = new Set<string>();
    const deduped = all.filter(c => { if (seen.has(c.id)) return false; seen.add(c.id); return true; });
    setConvs(deduped);
    // update selected status if changed
    setSelected(prev => prev ? (deduped.find(c => c.id === prev.id) || prev) : null);
  }, [user.id]);

  useEffect(() => {
    loadConvs();
    const t = setInterval(loadConvs, 15_000);
    return () => clearInterval(t);
  }, [loadConvs]);

  // Load phone list for filter / badges
  useEffect(() => {
    axios.get(`${API}/whatsapp/phones`, auth).then(r => setPhones(r.data || [])).catch(() => {});
  }, []);

  // ── Agent queue WS — refresh list on new message ──────────────────────────
  useEffect(() => {
    if (!token) return;
    const ws = connectAgentQueueWS(token);
    queueWsRef.current = ws;
    ws.on('escalation', () => { loadConvs(); playChime(); onNewMessage?.(); });
    return () => ws.disconnect();
  }, [token, loadConvs]);

  // ── Open conversation (auto-takeover if waiting) ──────────────────────────
  const openConversation = useCallback(async (conv: Conversation) => {
    convWsRef.current?.disconnect();
    setSelected(conv);
    setMessages([]);
    setInput('');

    // Auto-assign to this agent immediately
    let activeConv = conv;
    if (conv.status === 'WAITING_AGENT') {
      try {
        await axios.post(`${API}/agent/takeover`, { conversation_id: conv.id }, auth);
        activeConv = { ...conv, status: 'AGENT_ASSIGNED' };
        setSelected(activeConv);
        loadConvs();
      } catch { /* already assigned — continue */ }
    }

    const r = await axios.get(`${API}/conversations/${activeConv.id}/messages`, auth);
    setMessages(r.data || []);
    setTimeout(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); inputRef.current?.focus(); }, 80);

    const ws = connectConversationWS(activeConv.id, token, 'agent', String(user.id));
    convWsRef.current = ws;

    ws.on('agent_message', (d: any) => {
      const msg = { id: String(d.message_id), sender_type: 'AGENT' as const, message: d.message, timestamp: d.timestamp };
      setMessages(m => m.some(x => x.id === msg.id) ? m : [...m, msg]);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    });
    ws.on('customer_message', (d: any) => {
      const msg = { id: `cust-${d.timestamp}`, sender_type: 'CUSTOMER' as const, message: d.message, timestamp: d.timestamp };
      setMessages(m => m.some(x => x.id === msg.id) ? m : [...m, msg]);
      playChime();
      onNewMessage?.();
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    });
  }, [token, user.id, loadConvs]);

  useEffect(() => () => convWsRef.current?.disconnect(), []);

  // ── Send ──────────────────────────────────────────────────────────────────
  const sendMessage = async () => {
    if (!selected || !input.trim() || sending) return;
    const text = input.trim();
    setInput('');
    setSending(true);
    try {
      await axios.post(`${API}/agent/message`, { conversation_id: selected.id, message: text }, auth);
    } catch {
      setInput(text);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  // ── Close ─────────────────────────────────────────────────────────────────
  const closeConv = async () => {
    if (!selected) return;
    await axios.post(`${API}/conversations/${selected.id}/close`, {}, auth);
    await loadConvs();
    setSelected(null);
    setMessages([]);
  };

  const phoneMap = new Map(phones.map(p => [p.id, p]));

  const filtered = convs.filter(c =>
    (c.customer_name || c.customer_id).toLowerCase().includes(search.toLowerCase()) &&
    (filterPhone === null || c.whatsapp_phone_id === filterPhone)
  );

  const waiting = filtered.filter(c => c.status === 'WAITING_AGENT');
  const active  = filtered.filter(c => c.status === 'AGENT_ASSIGNED');
  const sorted  = [...waiting, ...active];

  const contactLabel = selected ? (selected.customer_name || `+${selected.customer_id}`) : '';

  const playChime = useCallback(() => { chime(); }, []);

  return (
    <div style={{ display: 'flex', height: '100%', fontFamily: "'Segoe UI', system-ui, sans-serif", overflow: 'hidden' }}>

      {/* ── LEFT: conversation list ── */}
      <div style={{ width: 340, display: 'flex', flexDirection: 'column', background: '#fff', borderRight: '1px solid #e9edef', flexShrink: 0 }}>

        {/* WA-style header */}
        <div style={{ background: '#f0f2f5', padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#25d366', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg viewBox="0 0 24 24" width="22" height="22" fill="white">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                <path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.549 4.107 1.509 5.838L0 24l6.335-1.484A11.955 11.955 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.006-1.37l-.36-.213-3.728.873.936-3.629-.234-.374A9.818 9.818 0 1112 21.818z"/>
              </svg>
            </div>
            <span style={{ fontWeight: 700, fontSize: 18, color: '#111b21' }}>WhatsApp</span>
          </div>
          {waiting.length > 0 && (
            <span style={{ background: '#25d366', color: '#fff', borderRadius: 12, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>
              {waiting.length} new
            </span>
          )}
        </div>

        {/* Search */}
        <div style={{ padding: '8px 12px', background: '#f0f2f5' }}>
          <div style={{ display: 'flex', alignItems: 'center', background: '#fff', borderRadius: 8, padding: '6px 12px', gap: 8 }}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="#8696a0">
              <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            </svg>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search conversations"
              style={{ border: 'none', outline: 'none', fontSize: 14, color: '#111b21', background: 'transparent', flex: 1, '::placeholder': { color: '#8696a0' } } as any}
            />
          </div>
          {/* Phone filter pills — only show when >1 number */}
          {phones.length > 1 && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8, overflowX: 'auto', paddingBottom: 2 }}>
              <button onClick={() => setFilter(null)} style={{
                padding: '3px 10px', borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
                background: filterPhone === null ? '#25d366' : '#e9edef', color: filterPhone === null ? '#fff' : '#54656f',
              }}>All</button>
              {phones.map(p => (
                <button key={p.id} onClick={() => setFilter(filterPhone === p.id ? null : p.id)} style={{
                  padding: '3px 10px', borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
                  background: filterPhone === p.id ? '#25d366' : '#e9edef', color: filterPhone === p.id ? '#fff' : '#54656f',
                }}>{p.display_name}</button>
              ))}
            </div>
          )}
        </div>

        {/* Conversation list */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {sorted.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#8696a0', fontSize: 13 }}>
              No WhatsApp messages yet
            </div>
          ) : sorted.map(conv => {
            const isWait = conv.status === 'WAITING_AGENT';
            const isMe   = conv.status === 'AGENT_ASSIGNED';
            const name   = conv.customer_name || `+${conv.customer_id}`;
            const isSelected = selected?.id === conv.id;

            return (
              <div key={conv.id} onClick={() => openConversation(conv)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 16px',
                  background: isSelected ? '#f0f2f5' : '#fff',
                  borderBottom: '1px solid #f0f2f5',
                  cursor: 'pointer',
                  transition: 'background 0.1s',
                }}>

                {/* Avatar */}
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <div style={{
                    width: 46, height: 46, borderRadius: '50%',
                    background: isWait ? '#25d366' : '#6366f1',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 18, fontWeight: 700, color: '#fff',
                  }}>
                    {avatar(name)}
                  </div>
                  {isWait && (
                    <div style={{
                      position: 'absolute', bottom: 0, right: 0,
                      width: 12, height: 12, borderRadius: '50%',
                      background: '#f59e0b', border: '2px solid #fff',
                    }} />
                  )}
                  {isMe && (
                    <div style={{
                      position: 'absolute', bottom: 0, right: 0,
                      width: 12, height: 12, borderRadius: '50%',
                      background: '#25d366', border: '2px solid #fff',
                    }} />
                  )}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                    <span style={{ fontWeight: 600, fontSize: 15, color: '#111b21', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {name}
                    </span>
                    <span style={{ fontSize: 11, color: '#8696a0', flexShrink: 0, marginLeft: 8 }}>
                      {fmtListTime(conv.created_at)}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: '#8696a0', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    {isWait ? (
                      <span style={{ background: '#fff8e1', color: '#b45309', borderRadius: 4, padding: '1px 6px', fontSize: 11, fontWeight: 600 }}>
                        Waiting
                      </span>
                    ) : (
                      <span style={{ background: '#f0fdf4', color: '#166534', borderRadius: 4, padding: '1px 6px', fontSize: 11, fontWeight: 600 }}>
                        Active
                      </span>
                    )}
                    {conv.whatsapp_phone_id && phoneMap.has(conv.whatsapp_phone_id) && (
                      <span style={{ background: '#eff6ff', color: '#1d4ed8', borderRadius: 4, padding: '1px 6px', fontSize: 11, fontWeight: 600 }}>
                        {phoneMap.get(conv.whatsapp_phone_id)!.display_name}
                      </span>
                    )}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      +{conv.customer_id}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── RIGHT: chat area ── */}
      {selected ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

          {/* Chat header */}
          <div style={{ background: '#075e54', padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#25d366', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
              {avatar(contactLabel)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>{contactLabel}</div>
              <div style={{ fontSize: 12, color: '#d1fae5' }}>
                {selected.whatsapp_phone_id && phoneMap.has(selected.whatsapp_phone_id)
                  ? phoneMap.get(selected.whatsapp_phone_id)!.display_name
                  : 'WhatsApp'}
              </div>
            </div>
            <button onClick={closeConv} style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Close Chat
            </button>
          </div>

          {/* Messages — WhatsApp chat background */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '12px 8%',
            background: '#efeae2',
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c9c0b5' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            display: 'flex', flexDirection: 'column', gap: 4,
          }}>
            {messages.length === 0 && (
              <div style={{ textAlign: 'center', color: '#8696a0', fontSize: 13, marginTop: 40, background: 'rgba(255,255,255,0.7)', borderRadius: 8, padding: '8px 16px', alignSelf: 'center' }}>
                No messages yet
              </div>
            )}
            {messages.filter(m => m.sender_type !== 'SYSTEM').map((m, i) => (
              <Bubble key={m.id || i} msg={m} contactName={contactLabel} />
            ))}
            <div ref={bottomRef} />
          </div>

          {/* Input bar — always visible */}
          <div style={{ background: '#f0f2f5', padding: '8px 12px', display: 'flex', alignItems: 'flex-end', gap: 8 }}>
            <div style={{ flex: 1, background: '#fff', borderRadius: 24, padding: '8px 16px', display: 'flex', alignItems: 'flex-end' }}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => { setInput(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder="Type a message"
                rows={1}
                style={{ flex: 1, border: 'none', outline: 'none', resize: 'none', fontSize: 15, color: '#111b21', background: 'transparent', lineHeight: 1.5, maxHeight: 120, fontFamily: 'inherit', overflow: 'hidden' }}
              />
            </div>
            <button
              onClick={sendMessage}
              disabled={sending || !input.trim()}
              style={{
                width: 46, height: 46, borderRadius: '50%', border: 'none', cursor: sending || !input.trim() ? 'default' : 'pointer',
                background: sending || !input.trim() ? '#8696a0' : '#25d366',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.15s', flexShrink: 0,
              }}
            >
              <svg viewBox="0 0 24 24" width="22" height="22" fill="white">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            </button>
          </div>
        </div>
      ) : (
        /* Empty state */
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f0f2f5', gap: 16 }}>
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#25d36620', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg viewBox="0 0 24 24" width="46" height="46" fill="#25d366">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
              <path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.549 4.107 1.509 5.838L0 24l6.335-1.484A11.955 11.955 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.006-1.37l-.36-.213-3.728.873.936-3.629-.234-.374A9.818 9.818 0 1112 21.818z"/>
            </svg>
          </div>
          <div style={{ fontSize: 22, fontWeight: 300, color: '#41525d' }}>WhatsApp Inbox</div>
          <div style={{ fontSize: 14, color: '#8696a0', textAlign: 'center', maxWidth: 300, lineHeight: 1.6 }}>
            {waiting.length > 0
              ? `${waiting.length} customer${waiting.length > 1 ? 's' : ''} waiting — select a conversation to reply`
              : 'Select a conversation from the list or wait for new messages'}
          </div>
        </div>
      )}
    </div>
  );
}
